import { renderParcelHoverTooltipHtml } from '../../Map/utils/parcelHoverTooltip';
import {
  normalizeListingImagesFromPayload,
  resolveCoverImageUrl,
} from '../../Map/utils/listingImageUtils';
import { normalizeListingVideosFromPayload } from '../../Map/utils/listingVideoUtils';
import { normalizeListingAttachmentsFromPayload } from '../../Map/utils/listingAttachmentUtils';
import {
  CAD_KIND_DEFAULT,
  CAD_KIND_LABELS,
  CAD_KINDS,
  CAD_PARCEL_AREA_M2_MAX,
  CAD_PARCEL_COLLECTION_EDITOR_KEYS,
  CAD_STOCK_LABELS,
  CAD_STOCK_STATUSES,
  CAD_USE_LABELS,
} from '../data/cadConstants';
import { cadLotChipImageId } from './cadLotChip';
import {
  cadFeatureToneSeed,
  countCadSellableLots,
  filterCadSellableLots,
  getCadKind,
  isCadSellableLot,
} from './cadMasterplan';
import { getCadStockColor } from './cadStockStatus';
import { CAD_PARCEL_STOCK_STYLE } from '../../Map/styles/mapTheme';

function escapeCadTooltipHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function getCadParcelId(feature) {
  if (!feature) return null;
  const id = feature.id ?? feature.properties?.id;
  return id == null ? null : String(id);
}

/**
 * Nombre visible del lote (card, ficha). Preferí `title` comercial;
 * si falta, `name`. Chip y tooltip del mapa usan solo `name`.
 */
export function getCadParcelLabel(feature) {
  const props = feature?.properties || {};
  const raw = String(props.title || props.name || '').trim();
  if (!raw) return 'LOTE';
  if (/^lote\b/i.test(raw)) return raw.replace(/^lote\b/i, 'LOTE');
  if (/^\d+$/.test(raw)) return `LOTE ${raw}`;
  return raw;
}

/**
 * Título de tooltip / hover / chrome de detalle: siempre `properties.name`
 * (nunca `title`). Antepone “LOTE” si el name es solo dígitos.
 *
 * @param {object|null|undefined} feature
 * @returns {string}
 */
export function getCadParcelTooltipLabel(feature) {
  const raw = String(feature?.properties?.name || '').trim();
  if (!raw) return 'LOTE';
  if (/^lote\b/i.test(raw)) return raw.replace(/^lote\b/i, 'LOTE');
  if (/^\d+$/.test(raw)) return `LOTE ${raw}`;
  return raw;
}

/**
 * Asunto del form de consulta: name (como tooltip) + manzana si hay.
 * p.ej. `LOTE 15 · Mz. 3`
 *
 * @param {object|null|undefined} feature
 * @returns {string}
 */
export function getCadParcelInquirySubject(feature) {
  const label = getCadParcelTooltipLabel(feature);
  const block = formatCadBlock(feature?.properties?.block);
  return block ? `${label} · ${block}` : label;
}

/**
 * Copy de card en listados CAD: name primero; title comercial al lado si difiere.
 *
 * @param {object|null|undefined} feature
 * @returns {{ primary: string, subtitle: string }}
 */
export function getCadParcelListCardCopy(feature) {
  const name = String(feature?.properties?.name || '').trim();
  const title = String(feature?.properties?.title || '').trim();
  return {
    primary: getCadParcelTooltipLabel(feature),
    subtitle: title && title !== name ? title : '',
  };
}

/**
 * Texto del chip sobre el lote: `properties.name` tal cual (nunca `title`).
 * Lotes: suele ser el número (`"12"`, `"54B"`). Sin name → sin chip.
 *
 * @param {object|null|undefined} featureOrProps
 * @returns {string}
 */
export function formatCadLotMapLabel(featureOrProps) {
  const props = featureOrProps?.properties && typeof featureOrProps.properties === 'object'
    ? featureOrProps.properties
    : (featureOrProps || {});
  return String(props.name || '').trim();
}

/**
 * Copia el FeatureCollection con `map_label`, `chip_image` y `tone_seed` por
 * feature, sin mutar los objetos de la lista.
 *
 * @param {GeoJSON.FeatureCollection|null|undefined} collection
 * @returns {GeoJSON.FeatureCollection}
 */
export function decorateCadParcelsForMap(collection) {
  const features = Array.isArray(collection?.features) ? collection.features : [];
  const toneBuckets = CAD_PARCEL_STOCK_STYLE.grassTones?.length || 1;
  return {
    type: 'FeatureCollection',
    features: features.map((feature) => {
      const cadKind = getCadKind(feature);
      const mapLabel = cadKind === 'lot' ? formatCadLotMapLabel(feature) : '';
      return {
        ...feature,
        properties: {
          ...(feature?.properties || {}),
          cad_kind: cadKind,
          map_label: mapLabel,
          tone_seed: cadFeatureToneSeed(getCadParcelId(feature), toneBuckets),
          chip_image: mapLabel
            ? cadLotChipImageId(feature?.properties?.stock_status, mapLabel)
            : '',
        },
      };
    }),
  };
}

export function getCadStockLabel(status) {
  if (!status) return CAD_STOCK_LABELS.available;
  return CAD_STOCK_LABELS[status] || status;
}

export function getCadUseLabel(use) {
  if (!use) return '—';
  return CAD_USE_LABELS[use] || use;
}

/**
 * Superficie para UI (card, tooltip, ficha). Entero en m²; sin basura de float
 * del parse DXF (p. ej. `360.173828125` → `360 m²`).
 *
 * @param {unknown} areaM2
 * @returns {string}
 */
export function formatCadArea(areaM2) {
  if (areaM2 == null || areaM2 === '') return '—';
  const n = Number(areaM2);
  if (!Number.isFinite(n)) return '—';
  return `${Math.round(n).toLocaleString('es-AR')} m²`;
}

/**
 * Valor del input de superficie en manage: mismo redondeo visual, sin “m²”.
 *
 * @param {unknown} areaM2
 * @returns {string}
 */
export function formatCadAreaInputValue(areaM2) {
  if (areaM2 == null || areaM2 === '') return '';
  const n = Number(areaM2);
  if (!Number.isFinite(n)) return '';
  return String(Math.round(n));
}

/**
 * Superficie comercial para `PATCH /updatecadparcel`. Vacío = omitir la
 * clave (no mandar `null`). 0, negativo o no número = error.
 *
 * @param {unknown} value
 * @returns {{ omit: true } | { error: string } | { area_m2: number }}
 */
export function parseCadParcelAreaM2(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return { omit: true };
  const normalized = raw.includes(',') && !raw.includes('.')
    ? raw.replace(',', '.')
    : raw;
  const n = Number(normalized);
  if (!Number.isFinite(n) || n <= 0 || n > CAD_PARCEL_AREA_M2_MAX) {
    return {
      error: 'La superficie tiene que ser un número mayor a 0 y de hasta 1.000.000 m².',
    };
  }
  return { area_m2: n };
}

/**
 * Solo dígitos y un separador decimal (`.` o `,`). Las letras no entran.
 *
 * @param {unknown} value
 * @returns {string}
 */
export function sanitizeCadDecimalInput(value) {
  const raw = String(value ?? '');
  let sep = '';
  let out = '';
  for (const ch of raw) {
    if (ch >= '0' && ch <= '9') {
      out += ch;
    } else if ((ch === ',' || ch === '.') && !sep) {
      sep = ch;
      out += ch;
    }
  }
  return out;
}

export function formatCadPrice(price, currency) {
  const n = Number(price);
  if (!Number.isFinite(n)) return '—';
  const code = (currency || 'USD').toUpperCase();
  try {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: code,
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `${code} ${n.toLocaleString('es-AR', { maximumFractionDigits: 0 })}`;
  }
}

/**
 * Manzana del loteo (`properties.block`) para meta de card / tooltip.
 * @param {unknown} block
 * @returns {string} p.ej. `Mz. 3`, o `''` si vacío
 */
export function formatCadBlock(block) {
  const raw = String(block ?? '').trim();
  if (!raw) return '';
  return `Mz. ${raw}`;
}

/**
 * Línea de meta de la card: manzana · superficie · precio · estado (sin chip).
 * @param {object|null|undefined} props
 * @returns {string}
 */
export function formatCadParcelListMeta(props) {
  const block = formatCadBlock(props?.block);
  const area = formatCadArea(props?.area_m2);
  const price = formatCadPrice(props?.price, props?.currency);
  const status = getCadStockLabel(props?.stock_status || 'available');
  return [block, area, price, status]
    .filter((part) => part && part !== '—')
    .join(' · ');
}

/**
 * Galería de la parcela CAD (`properties.photos` + `cover_image`).
 * @param {object|null|undefined} featureOrProps
 * @returns {object[]}
 */
export function getCadParcelPhotos(featureOrProps) {
  const props = featureOrProps?.properties && typeof featureOrProps.properties === 'object'
    ? featureOrProps.properties
    : (featureOrProps || {});
  return normalizeListingImagesFromPayload({
    photos: props.photos,
    cover_image: props.cover_image ?? null,
  });
}

/**
 * Patch local para `applyCadParcelPatch` tras subir / borrar / elegir portada.
 * @param {object[]|object} imagesOrPayload
 * @returns {{ photos: object[], cover_image: object|null }}
 */
export function buildCadParcelPhotosPatch(imagesOrPayload) {
  const photos = normalizeListingImagesFromPayload(
    Array.isArray(imagesOrPayload)
      ? { photos: imagesOrPayload }
      : (imagesOrPayload || {}),
  );
  const cover = photos.find((image) => image?.is_cover) || null;
  return { photos, cover_image: cover };
}

/**
 * URL de portada (ficha o card de Registros).
 * @param {object|null|undefined} featureOrProps
 * @returns {string|null}
 */

/**
 * Videos YouTube de la parcela CAD (`properties.videos`).
 * @param {object|null|undefined} featureOrProps
 * @returns {object[]}
 */
export function getCadParcelVideos(featureOrProps) {
  const props = featureOrProps?.properties && typeof featureOrProps.properties === 'object'
    ? featureOrProps.properties
    : (featureOrProps || {});
  return normalizeListingVideosFromPayload(props.videos);
}

/**
 * Patch local para `applyCadParcelPatch` tras agregar / borrar videos.
 * @param {object[]|object} videosOrPayload
 * @returns {{ videos: object[] }}
 */
export function buildCadParcelVideosPatch(videosOrPayload) {
  const videos = normalizeListingVideosFromPayload(videosOrPayload);
  return { videos };
}

/**
 * Adjuntos imagen/PDF de la parcela CAD (`properties.attachments`).
 * @param {object|null|undefined} featureOrProps
 * @returns {object[]}
 */
export function getCadParcelAttachments(featureOrProps) {
  const props = featureOrProps?.properties && typeof featureOrProps.properties === 'object'
    ? featureOrProps.properties
    : (featureOrProps || {});
  return normalizeListingAttachmentsFromPayload(props.attachments);
}

/**
 * Patch local para `applyCadParcelPatch` tras subir / borrar adjuntos.
 * @param {object[]|object} attachmentsOrPayload
 * @returns {{ attachments: object[] }}
 */
export function buildCadParcelAttachmentsPatch(attachmentsOrPayload) {
  const attachments = normalizeListingAttachmentsFromPayload(attachmentsOrPayload);
  return { attachments };
}

export function getCadParcelCoverUrl(featureOrProps) {
  const props = featureOrProps?.properties && typeof featureOrProps.properties === 'object'
    ? featureOrProps.properties
    : (featureOrProps || {});
  return resolveCoverImageUrl({
    cover_image: props.cover_image,
    photos: props.photos,
    images: props.images,
  });
}

/**
 * Título de la lista CAD en UI compartida y panel Listas (mayúsculas).
 * @param {string|{ name?: string, slug?: string }|null|undefined} projectOrName
 * @returns {string}
 */
export function formatCadProjectDisplayName(projectOrName) {
  const raw = typeof projectOrName === 'string'
    ? projectOrName
    : (projectOrName?.name || projectOrName?.slug || '');
  return (raw || 'Lista').toUpperCase();
}

/**
 * Tooltip de hover sobre una parcela CAD. Reusa el chrome de Terrio
 * (`terrio-parcel-tooltip`): título + una línea de meta.
 * @param {object|null} feature
 * @returns {string|null}
 */
export function buildCadParcelTooltipHtml(feature) {
  if (!feature) return null;
  const props = feature.properties || {};
  const area = formatCadArea(props.area_m2);
  const price = formatCadPrice(props.price, props.currency);
  const meta = [
    formatCadBlock(props.block) || null,
    getCadStockLabel(props.stock_status || 'available'),
    area !== '—' ? area : null,
    price !== '—' ? price : null,
  ].filter(Boolean).join(' · ');
  return renderParcelHoverTooltipHtml({
    title: getCadParcelTooltipLabel(feature),
    meta,
  });
}

/**
 * Tooltip de `/#/lista/:id`: card apilada (nombre, m², estado, precio) y
 * “Ver detalle”. No reusa el alto fijo de 54px del hover catastral.
 *
 * @param {object|null} feature
 * @returns {string|null}
 */
export function buildCadShareParcelTooltipHtml(feature) {
  if (!feature) return null;
  const props = feature.properties || {};
  const parcelId = getCadParcelId(feature);
  const title = getCadParcelTooltipLabel(feature);
  const area = formatCadArea(props.area_m2);
  const price = formatCadPrice(props.price, props.currency);
  const status = props.stock_status || 'available';
  const statusLabel = getCadStockLabel(status);
  const statusColor = getCadStockColor(status);
  const rows = [
    `<div class="terrio-cad-share-tooltip__title">${escapeCadTooltipHtml(title)}</div>`,
  ];
  const block = formatCadBlock(props.block);
  if (block) {
    rows.push(`<div class="terrio-cad-share-tooltip__muted">${escapeCadTooltipHtml(block)}</div>`);
  }
  if (area !== '—') {
    rows.push(`<div class="terrio-cad-share-tooltip__muted">${escapeCadTooltipHtml(area)}</div>`);
  }
  rows.push(
    `<div class="terrio-cad-share-tooltip__status">`
    + `<span class="terrio-cad-share-tooltip__dot" style="background:${statusColor}" aria-hidden="true"></span>`
    + `<span>${escapeCadTooltipHtml(statusLabel)}</span>`
    + `</div>`,
  );
  if (price !== '—') {
    rows.push(`<div class="terrio-cad-share-tooltip__price">${escapeCadTooltipHtml(price)}</div>`);
  }
  rows.push(
    `<div class="terrio-cad-share-tooltip__footer">Click para ver detalle <span aria-hidden="true">→</span></div>`,
  );
  const idAttr = parcelId
    ? ` data-cad-parcel-id="${escapeCadTooltipHtml(parcelId)}"`
    : '';
  return `<div class="terrio-cad-share-tooltip__card" role="button" tabindex="0"${idAttr}>${rows.join('')}</div>`;
}

export function findCadFeature(featureCollection, parcelId) {
  if (!featureCollection?.features || parcelId == null) return null;
  const id = String(parcelId);
  return featureCollection.features.find((feature) => getCadParcelId(feature) === id) || null;
}

/**
 * @param {object[]} features
 * @param {string} [query]
 */
export function filterCadParcelsByQuery(features, query) {
  const list = Array.isArray(features) ? features : [];
  const q = String(query || '').trim().toLowerCase();
  if (!q) return list;
  return list.filter((feature) => {
    const props = feature?.properties || {};
    const kind = getCadKind(feature);
    const haystack = [
      props.name,
      props.title,
      props.block,
      getCadParcelTooltipLabel(feature),
      getCadParcelLabel(feature),
      getCadParcelId(feature),
      props.description,
      props.position_label,
      kind !== 'lot' ? CAD_KIND_LABELS[kind] : '',
    ].filter(Boolean).join(' ').toLowerCase();
    return haystack.includes(q);
  });
}

/**
 * Estados elegidos, como lista. Acepta el string suelto que usaba el
 * dropdown de un solo valor. Sin estados válidos → `null` (todos).
 * @param {string|string[]|null|undefined} statusFilter
 * @returns {string[]|null}
 */
export function normalizeCadStatusFilter(statusFilter) {
  const list = Array.isArray(statusFilter) ? statusFilter : [statusFilter];
  const ids = [...new Set(list.filter((id) => CAD_STOCK_STATUSES.includes(id)))];
  return ids.length ? ids : null;
}

/**
 * Filtra parcelas para el listado (búsqueda + estado). El overlay usa
 * `markCadParcelsForOverlay` para no vaciar las geometrías de referencia.
 * @param {object[]} features
 * @param {{ query?: string, statusFilter?: string|string[]|null }} [opts]
 */
export function filterCadParcelsForOverlay(features, { query = '', statusFilter = null } = {}) {
  const searched = filterCadParcelsByQuery(filterCadSellableLots(features), query);
  const statuses = normalizeCadStatusFilter(statusFilter);
  if (!statuses) return searched;
  return searched.filter(
    (feature) => statuses.includes(feature?.properties?.stock_status || 'available'),
  );
}

/**
 * Listado de manage: por defecto solo lotes (búsqueda + stock).
 * `includeContext` agrega calles / verdes / predio (solo búsqueda); el
 * panel de manage lo deja en false — el contexto se abre desde el mapa.
 *
 * @param {object[]} features
 * @param {{ query?: string, statusFilter?: string|string[]|null, includeContext?: boolean }} [opts]
 */
export function filterCadParcelsForManageList(features, {
  query = '',
  statusFilter = null,
  includeContext = false,
} = {}) {
  const lots = filterCadParcelsForOverlay(features, { query, statusFilter });
  if (!includeContext) return lots;
  const context = filterCadParcelsByQuery(
    (Array.isArray(features) ? features : []).filter((feature) => !isCadSellableLot(feature)),
    query,
  );
  return [...lots, ...context];
}

/**
 * Body de `PATCH /updatecadparcel` desde el form de ficha.
 * Editor: solo listing core. Owner: `name` + `cad_kind`; si no es lote, no
 * manda `use` ni `stock_status` para que el API ponga amenity + unavailable.
 *
 * @param {object} form
 * @param {{ listingCoreOnly?: boolean }} [opts]
 */
export function buildCadParcelUpdateBody(form, { listingCoreOnly = false } = {}) {
  const toNumberOrNull = (value) => {
    if (value === '' || value == null) return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  };
  const core = {
    title: String(form?.title || '').trim() || null,
    description: String(form?.description || '').trim() || null,
    stock_status: form?.stock_status || 'available',
    price: toNumberOrNull(form?.price),
    currency: String(form?.currency || '').trim() || null,
    block: String(form?.block || '').trim() || null,
  };
  const area = parseCadParcelAreaM2(form?.area_m2);
  if (!area.omit && !area.error) core.area_m2 = area.area_m2;
  if (listingCoreOnly) {
    return CAD_PARCEL_COLLECTION_EDITOR_KEYS.reduce((acc, key) => {
      if (Object.prototype.hasOwnProperty.call(core, key)) acc[key] = core[key];
      return acc;
    }, {});
  }
  const kind = CAD_KINDS.includes(form?.cad_kind) ? form.cad_kind : CAD_KIND_DEFAULT;
  const body = {
    ...core,
    name: String(form?.name || '').trim(),
    cad_kind: kind,
    use: form?.use || null,
    orientation: String(form?.orientation || '').trim() || null,
    position_label: String(form?.position_label || '').trim() || null,
    fos: toNumberOrNull(form?.fos),
    fot: toNumberOrNull(form?.fot),
  };
  if (kind !== 'lot') {
    delete body.use;
    delete body.stock_status;
  }
  return body;
}

/**
 * Todas las parcelas al overlay: `filter_match` marca las que cumplen
 * búsqueda/estado. Sin filtro activo, deja las features como están
 * (el paint las trata como match).
 * @param {object[]} features
 * @param {{ query?: string, statusFilter?: string|string[]|null }} [opts]
 */
export function markCadParcelsForOverlay(features, { query = '', statusFilter = null } = {}) {
  const list = Array.isArray(features) ? features : [];
  const hasFilter = Boolean(String(query || '').trim() || normalizeCadStatusFilter(statusFilter));
  if (!hasFilter) return list;
  const matchedIds = new Set(
    filterCadParcelsForOverlay(list, { query, statusFilter }).map((feature) => getCadParcelId(feature)),
  );
  return list.map((feature) => ({
    ...feature,
    properties: {
      ...(feature?.properties || {}),
      filter_match: isCadSellableLot(feature)
        ? matchedIds.has(getCadParcelId(feature))
        : true,
    },
  }));
}

/**
 * @param {{ total?: number, available?: number }} counts
 */
export function formatCadParcelsCountsLabel({ total = 0, available = 0 } = {}) {
  const totalN = Number(total) || 0;
  const availableN = Number(available) || 0;
  const parcelsPart = totalN === 1 ? '1 lote' : `${totalN} lotes`;
  const availablePart = availableN === 1 ? '1 disponible' : `${availableN} disponibles`;
  return `${parcelsPart} · ${availablePart}`;
}

/**
 * `parcel_count` del índice y del resumen del GET: solo `cad_kind: lot`
 * (o sin kind, proyectos viejos). Calle / verde / predio no entran.
 * Si el payload no trae el campo y sí trae GeoJSON, se cuenta en cliente.
 *
 * @param {object|null|undefined} project
 * @returns {number|null}
 */
export function readCadProjectParcelCount(project) {
  if (project?.parcel_count != null && project.parcel_count !== '') {
    const n = Number(project.parcel_count);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  const features = project?.parcels?.features;
  if (Array.isArray(features) && features.length > 0) {
    return countCadSellableLots(features);
  }
  return null;
}

/**
 * Alinea `parcel_count` con los lotes del GeoJSON (PATCH de `cad_kind` o
 * borrar un lote) para que card y pin no esperen el próximo GET del índice.
 *
 * @param {object|null|undefined} project
 * @returns {object|null|undefined}
 */
export function withCadLotParcelCount(project) {
  if (!project) return project;
  const features = project.parcels?.features;
  if (!Array.isArray(features)) return project;
  return { ...project, parcel_count: countCadSellableLots(features) };
}

/**
 * Meta de la card CAD en Listas: `N lotes`. Invitado: fecha de compartida.
 *
 * @param {object|null|undefined} project
 * @param {{ guest?: boolean }} [options]
 * @returns {string}
 */
export function formatCadCollectionIndexMeta(project, { guest = false } = {}) {
  if (guest) {
    if (project?.shared_with_me_at) {
      return `Compartida el ${new Date(project.shared_with_me_at).toLocaleDateString()}`;
    }
    return 'Lista compartida';
  }
  const total = readCadProjectParcelCount(project);
  if (total == null) return '';
  return total === 1 ? '1 lote' : `${total} lotes`;
}

function cadNumeric(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Ordena parcelas CAD. Valores sin número van al final.
 * `inquiries-*` usa `getInquiryTotal` (dueño/editor). Cero cuenta como 0.
 * @param {object[]} features
 * @param {string} sortBy
 * @param {{ getInquiryTotal?: (feature: object) => number }} [options]
 */
export function sortCadParcels(features, sortBy, options = {}) {
  const list = Array.isArray(features) ? [...features] : [];
  const byInquiries = sortBy === 'inquiries-desc' || sortBy === 'inquiries-asc';
  const dir = sortBy === 'price-desc' || sortBy === 'area-desc' || sortBy === 'inquiries-desc' ? -1 : 1;
  const prop = sortBy?.startsWith('price') ? 'price' : sortBy?.startsWith('area') ? 'area_m2' : null;
  const getInquiryTotal = options.getInquiryTotal;

  list.sort((a, b) => {
    if (byInquiries) {
      const va = Math.max(0, Number(getInquiryTotal?.(a)) || 0);
      const vb = Math.max(0, Number(getInquiryTotal?.(b)) || 0);
      const diff = dir * (va - vb);
      if (diff !== 0) return diff;
    } else if (prop) {
      const va = cadNumeric(a?.properties?.[prop]);
      const vb = cadNumeric(b?.properties?.[prop]);
      if (va == null && vb == null) {
        return getCadParcelTooltipLabel(a).localeCompare(getCadParcelTooltipLabel(b), 'es');
      }
      if (va == null) return 1;
      if (vb == null) return -1;
      const diff = dir * (va - vb);
      if (diff !== 0) return diff;
    }
    return getCadParcelTooltipLabel(a).localeCompare(getCadParcelTooltipLabel(b), 'es');
  });
  return list;
}
